import { useState } from "react";

const portalFeatures = [
  {
    icon: "ri-pie-chart-2-line",
    title: "Portfolio Dashboard",
    desc: "Real-time overview of your investment performance, current valuations, income received, and projected returns.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Monthly Statements",
    desc: "Automated monthly income statements, annual summaries, and downloadable reports for tax and accounting purposes.",
  },
  {
    icon: "ri-customer-service-2-line",
    title: "Support Ticket System",
    desc: "Raise and track support tickets directly through the portal. Get responses from your dedicated investment manager within 24 hours.",
  },
  {
    icon: "ri-file-lock-line",
    title: "Document Vault",
    desc: "Secure access to your subscription agreement, Information Memorandum, KYC documents, and all correspondence.",
  },
  {
    icon: "ri-notification-3-line",
    title: "Distribution Alerts",
    desc: "Instant push notifications and email alerts when monthly income distributions are processed to your account.",
  },
  {
    icon: "ri-refresh-line",
    title: "Rollover Management",
    desc: "Manage your maturity preferences, elect rollover options, and update your investment instructions — all in-portal.",
  },
];

interface ComingSoonBadgeProps {
  children: React.ReactNode;
  tooltip?: string;
}

function ComingSoonBadge({ children, tooltip = "App launching soon — link will be live on publication" }: ComingSoonBadgeProps) {
  const [showTip, setShowTip] = useState(false);
  return (
    <div
      className="relative cursor-not-allowed"
      onMouseEnter={() => setShowTip(true)}
      onMouseLeave={() => setShowTip(false)}
    >
      <div className="opacity-60 pointer-events-none select-none">{children}</div>
      <span className="absolute -top-1.5 -right-1.5 bg-[#C9A84C] text-[#071A2E] text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none uppercase tracking-wide">
        Soon
      </span>
      {showTip && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 w-52 bg-[#071A2E] border border-[#C9A84C]/30 text-white text-xs rounded-xl px-3 py-2 text-center shadow-lg pointer-events-none">
          {tooltip}
          <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-[#071A2E]" />
        </div>
      )}
    </div>
  );
}

export default function PortalBanner() {
  return (
    <section className="bg-white py-20 md:py-28 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section header */}
        <div className="text-center mb-16">
          <span className="text-[#C9A84C] text-xs font-semibold uppercase tracking-widest block mb-3">
            Client Technology
          </span>
          <h2
            className="text-3xl md:text-4xl font-bold text-[#071A2E]"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Your Investment,
            <span className="text-[#C9A84C]"> In Your Hands</span>
          </h2>
          <p className="text-slate-500 text-sm mt-3 max-w-lg mx-auto">
            The Thorpe Dellow Client Portal gives you full visibility and control over your investment — available 24/7 on web, iOS, and Android.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center mb-20">
          {/* Left: Portal image */}
          <div className="relative">
            <div className="w-full h-[280px] md:h-[420px] rounded-3xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20investment%20portfolio%20dashboard%20on%20laptop%20screen%2C%20dark%20elegant%20UI%2C%20financial%20charts%2C%20clean%20data%20visualization%2C%20wealth%20management%20app%2C%20professional%20desktop%20interface%2C%20soft%20office%20lighting%2C%20high%20tech%20fintech%20platform&width=700&height=420&seq=700&orientation=landscape"
                alt="Thorpe Dellow client portal dashboard"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Floating stats */}
            <div className="absolute -bottom-5 right-2 md:right-6 bg-[#071A2E] rounded-2xl px-4 py-3 md:px-5 md:py-4 text-white">
              <p className="text-xs text-slate-400 mb-1">Active Investor Accounts</p>
              <p className="text-xl md:text-2xl font-bold text-[#C9A84C]" style={{ fontFamily: "'Playfair Display', serif" }}>
                2,400+
              </p>
              <p className="text-xs text-slate-400 mt-0.5">On the platform</p>
            </div>
            <div className="absolute top-5 left-2 md:left-0 bg-[#C9A84C] rounded-2xl px-4 py-3 md:px-5 md:py-4 text-[#071A2E]">
              <p className="text-xs font-semibold mb-1">Available On</p>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-global-line text-sm leading-none" />
                </div>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-apple-fill text-sm leading-none" />
                </div>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-android-line text-sm leading-none" />
                </div>
              </div>
            </div>
          </div>

          {/* Right: Features */}
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {portalFeatures.map((f) => (
                <div
                  key={f.title}
                  className="bg-[#EEF2F9] rounded-2xl p-5 hover:bg-[#E4ECF5] transition-colors"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#C9A84C]/15 text-[#C9A84C] mb-3">
                    <i className={`${f.icon} text-lg leading-none`} />
                  </div>
                  <p className="font-bold text-[#071A2E] text-sm mb-1">{f.title}</p>
                  <p className="text-slate-500 text-xs leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>

            {/* Portal CTA */}
            <div className="bg-[#071A2E] rounded-2xl p-6">
              <p className="text-white font-bold text-base mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                Access the Client Portal
              </p>
              <p className="text-slate-400 text-xs mb-5">
                Log in to portal.thorpedellow.com or download the app for iOS and Android.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://portal.thorpedellow.com"
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#C9A84C] text-[#071A2E] text-sm font-semibold hover:bg-[#B8953B] transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-global-line leading-none" />
                  Web Portal Login
                </a>
                <ComingSoonBadge>
                  <div className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white/10 text-white text-sm font-semibold whitespace-nowrap">
                    <i className="ri-apple-fill leading-none" />
                    App Store
                  </div>
                </ComingSoonBadge>
                <ComingSoonBadge>
                  <div className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white/10 text-white text-sm font-semibold whitespace-nowrap">
                    <i className="ri-google-play-line leading-none" />
                    Google Play
                  </div>
                </ComingSoonBadge>
              </div>
            </div>
          </div>
        </div>

        {/* Full-width App Banner */}
        <div
          className="relative rounded-3xl overflow-hidden py-16 px-6 md:px-14"
          style={{
            background: "linear-gradient(135deg, #071A2E 0%, #0D2B4A 50%, #071A2E 100%)",
          }}
        >
          {/* BG decoration */}
          <div className="absolute top-0 right-0 w-80 h-80 rounded-full bg-[#C9A84C]/10 -translate-y-1/2 translate-x-1/3 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-60 h-60 rounded-full bg-[#C9A84C]/5 translate-y-1/2 -translate-x-1/3 pointer-events-none" />

          <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-10">
            <div className="text-center lg:text-left">
              <p className="text-[#C9A84C] text-xs font-semibold uppercase tracking-widest mb-3">Mobile App</p>
              <h3
                className="text-2xl md:text-3xl font-bold text-white mb-3"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Manage Your Portfolio
                <br />Anywhere, Anytime
              </h3>
              <p className="text-slate-400 text-sm max-w-md">
                The Thorpe Dellow app puts your investment dashboard, distribution history, support tickets, and documents in your pocket — available for iPhone and Android.
              </p>
              <div className="mt-4 inline-flex items-center gap-2 bg-[#C9A84C]/15 border border-[#C9A84C]/30 text-[#C9A84C] text-xs font-semibold px-4 py-2 rounded-full">
                <i className="ri-time-line leading-none" />
                App launching soon — links will be updated on publication
              </div>
            </div>

            <div className="flex flex-col sm:flex-row lg:flex-col gap-4 flex-shrink-0">
              {/* Apple App Store Badge — Coming Soon */}
              <ComingSoonBadge tooltip="Thorpe Dellow iOS app launching soon — link will be live on App Store publication">
                <div className="flex items-center gap-4 bg-white/10 border border-white/20 rounded-2xl px-6 py-4">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-apple-fill text-white text-3xl leading-none" />
                  </div>
                  <div>
                    <p className="text-white/60 text-xs">Download on the</p>
                    <p className="text-white font-bold text-base">App Store</p>
                  </div>
                </div>
              </ComingSoonBadge>
              {/* Google Play Badge — Coming Soon */}
              <ComingSoonBadge tooltip="Thorpe Dellow Android app launching soon — link will be live on Google Play publication">
                <div className="flex items-center gap-4 bg-white/10 border border-white/20 rounded-2xl px-6 py-4">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-google-play-line text-white text-3xl leading-none" />
                  </div>
                  <div>
                    <p className="text-white/60 text-xs">Get it on</p>
                    <p className="text-white font-bold text-base">Google Play</p>
                  </div>
                </div>
              </ComingSoonBadge>
            </div>

            {/* QR placeholder */}
            <div className="hidden lg:flex flex-col items-center gap-2">
              <div className="w-28 h-28 bg-white rounded-2xl flex items-center justify-center">
                <div className="grid grid-cols-4 gap-1 p-2">
                  {Array.from({ length: 16 }).map((_, i) => (
                    <div
                      key={i}
                      className="w-4 h-4 rounded-sm"
                      style={{ backgroundColor: [0,1,2,4,6,8,10,12,14,15].includes(i) ? "#071A2E" : "#EEF2F9" }}
                    />
                  ))}
                </div>
              </div>
              <p className="text-slate-400 text-xs text-center">Available soon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
